
import {
  COD_FEE_INFO_TITLE,
} from '~~/constants/checkout';
import customFieldsToBankInfo from '~~/helpers/Checkout/customFieldsToBankInfo';
import { STORE_CUSTOM_FIELDS } from '~~/constants/commerceTools';
import type { Campaign } from '~~/types/store/campaign';
import { buildCampaignLabel, extractActiveCampaigns } from '~~/helpers/store/campaign';
import { countryCodes } from '~~/i18n/enums/countryCodes';
import { useCtStore } from '~/utils/ct';
import { customFieldsToContactInformation } from '~~/helpers/store/contactInformation';
import { findCustomFieldValue, isCustomFieldTruthy } from '~~/helpers/customFields';
import { useStoreCustomFields } from '~/composables/useStoreCustomFields';

export default function () {

  const useStoreInstance = useCtStore();
  const {
    load: oldLoad,
    response,
  } = useStoreInstance;
  const i18n = useI18n();

  const {
    orderNumber,
  } = useUserOrderExtended();

  const { isThankYouPage } = useIsPage();

  const loading = useState<boolean>('store-loading', () => true);
  const { customFieldsRaw, setCustomFieldsRaw } = useStoreCustomFields();

  const { countryLocalization, countryCode, languageAndCountry } = useLocalization();

  const codFeeTitle: ComputedRef<string> = computed(() => customFieldsRaw.value?.find(value =>
    value?.name === COD_FEE_INFO_TITLE)?.value);

  const cleanedOrderNumber: ComputedRef<Maybe<string>> = computed(() => {

    if (countryCode.value === countryCodes.CZ && orderNumber.value) {

      return orderNumber.value.replace('NS', '');
    
    }

    return orderNumber.value;
  
  });

  const hasGoogleReviewsEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.GOOGLE_REVIEWS_ENABLED),
  );

  const isExtraGuaranteeEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.EXTRA_GUARANTEE_ENABLED),
  );
  const isComplementarySidebarEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.COMPLEMENTARY_PRODUCTS_ENABLED),
  );
  const isAutocompleteEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.AUTOCOMPLETE_ENABLED),
  );
  const showTrustpilotScoreInHeader: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.SHOW_TRUSTPILOT_SCORE_IN_HEADER),
  );
  const isVatValidationEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.VAT_VALIDATION_ENABLED),
  );
  const isChangeTaxRateEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.CHANGE_TAX_RATE_ENABLED),
  );
  const isBillingAddressValidationEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.BILLING_ADDRESS_VALIDATION_ENABLED),
  );
  const isB2bFallbackTaxEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.USE_B2B_VAT_WHEN_INVALID),
  );
  const isLoyaltyDiscountEnabled: ComputedRef<boolean> = computed(() =>
    isCustomFieldTruthy(customFieldsRaw.value, STORE_CUSTOM_FIELDS.LOYALTY_DISCOUNT_ENABLED),
  );

  const inboundOffsetDays: ComputedRef<number> = computed(() => {

    const offsetValue = findCustomFieldValue(customFieldsRaw.value, STORE_CUSTOM_FIELDS.INBOUND_OFFSET_DAYS);

    return offsetValue ? parseInt(offsetValue, 10) || 0 : 0;

  });

  const bankInfo = computed(() => customFieldsToBankInfo(customFieldsRaw.value));

  const contactInformation = computed(() =>
    customFieldsToContactInformation(customFieldsRaw.value, languageAndCountry.value),
  );

  const bankInfoComplete = computed(() => bankInfo.value && [
    { label: i18n.t('Account holder:'), value: bankInfo.value.bankAccountOwner },
    { label: i18n.t('Bank:'), value: bankInfo.value.bankInstitute },
    {
      label: i18n.t('Reference:'),
      value: isThankYouPage.value && cleanedOrderNumber.value
        ? cleanedOrderNumber.value
        : i18n.t('Your order number'),
    },
    { label: i18n.t('BIC/SWIFT:'), value: bankInfo.value.bankBic },
    { label: i18n.t('IBAN:'), value: bankInfo.value.bankIban },
  ]);

  const campaigns: ComputedRef<Campaign[]> = computed(() =>
    JSON.parse(findCustomFieldValue(customFieldsRaw.value, STORE_CUSTOM_FIELDS.CAMPAIGNS) || '[]'),
  );

  const activeCampaigns: ComputedRef<Campaign[]> = computed(() =>
    extractActiveCampaigns(campaigns.value),
  );
  const isLoaded = computed(() =>
    !!customFieldsRaw?.value?.length,
  );

  const load = async () => {

    loading.value = true;
    await oldLoad({ key: countryLocalization.value.storeId });
    const customFieldsRawResponse: typeof customFieldsRaw['value'] = response.value?.stores?.results?.find(result =>
      result && result.key === countryLocalization.value.storeId)?.custom?.customFieldsRaw;

    if (customFieldsRawResponse) {

      setCustomFieldsRaw(customFieldsRawResponse);

    }

    loading.value = false;
  
  };

  const isCampaignLabel = (label: string): boolean => {

    return campaigns.value.some(campaign =>
      label === buildCampaignLabel(campaign.campaignName),
    );
  
  };

  const isActiveCampaignLabel = (label: string): boolean => {

    return activeCampaigns.value.some(campaign =>
      buildCampaignLabel(campaign.campaignName) === label,
    );
  
  };

  return {
    ...useStoreInstance,
    load,
    codFeeTitle,
    loading: computed(() => useStoreInstance.loading.value || loading.value),
    bankInfo,
    isLoaded,
    bankInfoComplete,
    contactInformation,
    isAutocompleteEnabled,
    showTrustpilotScoreInHeader,
    isVatValidationEnabled,
    isChangeTaxRateEnabled,
    isBillingAddressValidationEnabled,
    isB2bFallbackTaxEnabled,
    isExtraGuaranteeEnabled,
    isCampaignLabel,
    isActiveCampaignLabel,
    activeCampaigns,
    isComplementarySidebarEnabled,
    hasGoogleReviewsEnabled,
    cleanedOrderNumber,
    isLoyaltyDiscountEnabled,
    inboundOffsetDays,
  };

}
